import {RiseSurveyConfig} from '../../types';
import Analytics from '../../utils/Analytics';
import {ErrorBoundary} from 'components/ErrorBoundary/ErrorBoundary';
import {getGeoLocation} from 'externalAPIs/ipwhois';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import LoadingScreen from 'screens/LoadingScreen/LoadingScreen';
import {CategoryProgressBar} from './components/CategoryProgressBar/CategoryProgressBar';
import {RiseHeader} from './components/RiseHeader/RiseHeader';
import {registerRiseInterstitial} from './components/RiseInterstitialTemplate/RiseInterstitialBase';
import RiseSurveyControls from './components/RiseSurveyControls/RiseSurveyControls';
import {SegmentedProgressBar} from './components/SegmentedProgressBar/SegmentedProgressBar';
import {SocialProofFooterItem} from './components/SocialProofFooterItem/SocialProofFooterItem';
import {FunctionFactory, ITheme, Model, PageModel, SurveyModel} from 'survey-core';
import 'survey-core/defaultV2.min.css';
import {Survey} from 'survey-react-ui';
import './InsomniaSurvey.css';
import {riseSurveyTheme} from './InsomniaSurveyTheme';
import surveyTheme from './surveyTheme.json';
import {CategoryArray, getCategories, getCategoryName} from './utils/categoryUtils';
import {completeSurvey} from './utils/completeSurvey';
import {isInterstitial} from './utils/interstitial';
import {preloadImages} from './utils/preloadImages';
import {SurveyData} from './utils/surveyData';
import {surveySwitchFn} from './utils/switchExpression';
import {SurveyErrorProvider} from './contexts/SurveyErrorContext';
import {SurveyErrorDisplay} from './components/SurveyErrorDisplay/SurveyErrorDisplay';
import {getInsuranceOptionsForState} from 'surveys/insomnia_funnel/survey/utils';

/**
 * Setup SurveyJS instance
 */
export const surveyInstance = new Model();
surveyInstance.css = riseSurveyTheme;
surveyInstance.applyTheme(surveyTheme as ITheme);

// SurveyJS calls scrollToTopOnPageChange on every page render, which
// uses scrollIntoView({block: 'start'}) on the page element — this
// pushes the RISE header + progress bar off-screen because they sit
// above the page element in our shell. focusFirstQuestionAutomatic
// (already disabled in survey_config_base) covers the focus side of
// this, but the page-element scroll fires regardless. Noop it.
// eslint-disable-next-line @typescript-eslint/no-empty-function
(surveyInstance as unknown as {scrollToTopOnPageChange: () => void}).scrollToTopOnPageChange = () => {
  /* noop — keeps the RISE header + progress bar in view across page changes. */
};

registerRiseInterstitial();

// Register custom conditional functions
FunctionFactory.Instance.register('switch', surveySwitchFn);

/**
 * Handles duplicate page names in the survey config by appending numbers to make them unique.
 * Returns the new pages array.
 */
const handleDuplicatePageNames = (pages: any[]): any[] => {
  const seenNames = new Map<string, number>();
  let hadWarning = false;

  const newPages = pages.map(page => {
    if (!page.name) {
      return {...page};
    }

    const originalName = page.name;
    const count = seenNames.get(originalName) ?? 0;
    seenNames.set(originalName, count + 1);

    if (count > 0) {
      hadWarning = true;
      return {...page, name: `${originalName}_${count}`};
    }

    return {...page};
  });

  if (hadWarning) {
    console.warn('Had duplicate page names. This is probably a mistake.');
  }

  return newPages;
};

type RiseSurveyProps = {
  surveyConfigJson: RiseSurveyConfig;
};

const anyRequiredQuestions = (page: any) => {
  return page.elements.some((element: any) => element.isRequired);
};

const shouldAutoAdvance = (page: any) => {
  return Boolean(page.jsonObj?.riseProperties?.autoAdvance);
};

const shouldAddToIdentify = (page: any) => {
  return Boolean(page.jsonObj?.riseProperties?.identifyPropertyName);
};
/**
 * This is the heart of the Rise Survey system.
 * It sets up the survey, the header, and handles the controls.
 */

const InsomniaSurvey: React.FC<RiseSurveyProps> = ({surveyConfigJson}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLastPage, setIsLastPage] = useState(false);
  const [isInterstitialPage, setIsInterstitialPage] = useState(false);
  const [isFirstPage, setIsFirstPage] = useState(false);
  const [hideProgressBar, setHideProgressBar] = useState(false);
  const [hideCategory, setHideCategory] = useState(false);
  const [category, setCategory] = useState<string>('');
  const [isRequiredQuestion, setIsRequiredQuestion] = useState(true);
  const [isAutoAdvance, setIsAutoAdvance] = useState(true);
  const [transitionClass, setTransitionClass] = useState('fade-in');
  const enableAnswerNewStyle = surveyConfigJson.enableAnswerNewStyle;
  const [categories, setCategories] = useState<CategoryArray>(getCategories(surveyInstance));
  const hasCategories = categories?.some(Boolean);
  // Check sessionStorage flag on every render to catch browser back button
  // This ensures we reset the state before isComplete is computed
  // But we don't clear the instance here - that happens in the initialization useEffect
  const navigatingBackFlag = sessionStorage.getItem('insomnia-navigating-back-from-checkout') === 'true';
  const isComplete = navigatingBackFlag ? false : surveyInstance.state === 'completed';
  const navRef = useRef(navigate);
  const trackedPageViewsRef = useRef<Set<string>>(new Set());

  // Helper function to get redirect path
  const getRedirectPath = useCallback(() => {
    const redirectConfig = surveyConfigJson.onCompleteRedirectTo ?? '/checkout';

    // If the redirect is a function, call it with the survey instance
    if (typeof redirectConfig === 'function') {
      try {
        return redirectConfig(surveyInstance);
      } catch (error) {
        console.warn('Failed to execute redirect function:', error);
        return '/checkout'; // fallback
      }
    }

    return redirectConfig;
  }, [surveyConfigJson.onCompleteRedirectTo]);

  // This useEffect is used to navigate to the checkout page when the survey is complete
  // It handles the case where the survey is complete and the user refreshes the page,
  // or navigates back from the checkout to the survey it will forward them to the checkout.
  // The routing _ALSO_ happens in the onComplete event, but that event is not fired when the user
  // loads the page, only when they actually complete the survey.
  useEffect(() => {
    if (isComplete) {
      // Check if user is navigating back from checkout (browser back button or UI back button)
      const navigatingBack = sessionStorage.getItem('insomnia-navigating-back-from-checkout') === 'true';
      if (navigatingBack) {
        // Clear the flag
        sessionStorage.removeItem('insomnia-navigating-back-from-checkout');
        // User is coming back to edit, don't redirect
        return;
      }

      // Check saved data - if completed is false, allow editing (don't redirect)
      const savedData = SurveyData.read();
      if (savedData?.completed === false) {
        // User is coming back to edit, don't redirect
        return;
      }
      navRef.current(getRedirectPath());
    }
  }, [isComplete, getRedirectPath]);

  useEffect(() => {
    const imagesToPreload = surveyConfigJson.preloadImages ?? [];
    preloadImages(imagesToPreload);
  }, [surveyConfigJson.preloadImages]);

  useEffect(() => {
    const referralCode = surveyInstance.getValue('referral_code');
    Analytics.track('Survey Started', {
      survey_name: surveyConfigJson.title,
      surveyTitle: surveyConfigJson.title,
      referral_code: referralCode || undefined,
    });
  }, [surveyConfigJson]);

  useEffect(() => {
    const trackKeyPageView = (sender: SurveyModel, page: PageModel) => {
      const eventNameByPageName: Record<string, string> = {
        'page-name_collection': 'Clinic Web Intake Name Screen Viewed',
      };
      const pageName = page.name;
      const eventName = eventNameByPageName[pageName];

      if (!eventName || trackedPageViewsRef.current.has(pageName)) {
        return;
      }

      trackedPageViewsRef.current.add(pageName);
      Analytics.track(eventName, {
        survey_name: sender.title,
        page_name: pageName,
        page_title: page.title,
        pageNumber: sender.currentPageNo,
      });
    };

    const shouldTrackAnswerSelectedOnChange = (question: any) => question.question?.getType?.() !== 'text';

    const handleCurrentPageChanged = (sender: SurveyModel) => {
      const page = sender.currentPage;
      const isLastPage = sender.isLastPage;
      const isFirstPage = sender.isFirstPage;
      setIsLastPage(isLastPage);
      setIsFirstPage(isFirstPage);
      setCategories(getCategories(sender));
      setIsRequiredQuestion(anyRequiredQuestions(sender.currentPage));
      setIsAutoAdvance(shouldAutoAdvance(sender.currentPage));
      setIsInterstitialPage(isInterstitial(page));
      setHideProgressBar(page.jsonObj?.riseProperties?.hideProgressBar);
      setHideCategory(page.jsonObj?.riseProperties?.hideCategory);
      setCurrentQuestionIndex(sender.currentPageNo);
      setCategory(getCategoryName(page));
      setTransitionClass('fade-in');
      trackKeyPageView(sender, page);
      // Persist the new page number so a refresh restores the user to
      // their actual current page. Without this, only `handleValueChanged`
      // saves — and that fires BEFORE `nextPage()` advances, so
      // localStorage lags one page behind. Interstitials (education,
      // primer, paywall) call `nextPage()` directly via their own CTAs
      // and would otherwise never trigger a save.
      SurveyData.save(sender);
    };

    const setInitialSurveyState = (sender: SurveyModel) => {
      const page = sender.currentPage;
      const isLastPage = sender.isLastPage;
      const isFirstPage = sender.isFirstPage;
      setCategories(getCategories(sender));
      setIsRequiredQuestion(anyRequiredQuestions(sender.currentPage));
      setIsAutoAdvance(shouldAutoAdvance(sender.currentPage));
      setIsFirstPage(isFirstPage);
      setIsLastPage(isLastPage);
      setIsInterstitialPage(isInterstitial(page));
      setHideProgressBar(page.jsonObj?.riseProperties?.hideProgressBar);
      setHideCategory(page.jsonObj?.riseProperties?.hideCategory);
      setCategory(getCategoryName(page));
    };

    const handleValueChanged = (sender: SurveyModel, question: any) => {
      // Guard against calculated values or other non-question changes
      if (!question.question) {
        return;
      }

      const currentPage = sender.currentPage;
      const questionPageName = question.question?.page?.name;
      const currentPageName = currentPage?.name;
      const autoAdvance = shouldAutoAdvance(currentPage) && questionPageName && questionPageName === currentPageName;
      const addToIdentify = shouldAddToIdentify(sender.currentPage);
      if (shouldTrackAnswerSelectedOnChange(question)) {
        Analytics.track('Answer selected', {
          survey_name: surveyInstance.title,
          question_title: question.question.title,
          question_name: question.name,
          question_value: question.value,
          pageNumber: sender.currentPageNo,
        });
      }
      if (addToIdentify) {
        const identifyProperty = sender.currentPage.jsonObj?.riseProperties?.identifyPropertyName;
        Analytics.identify({['web_' + identifyProperty]: question.value});
      }
      SurveyData.save(sender);

      if (autoAdvance) {
        setTransitionClass('fade-out');
        setTimeout(() => {
          if (!sender.isLastPage) {
            sender.nextPage();
          } else {
            completeSurvey(sender);
          }
        }, 400);
      }
    };

    const handleComplete = async () => {
      // Call the onComplete callback if provided, before tracking and redirecting
      if (surveyConfigJson.onComplete) {
        try {
          await surveyConfigJson.onComplete(surveyInstance.data);
        } catch (error) {
          console.error('Error in onComplete callback:', error);
          // Continue with completion even if callback fails
        }
      }

      const referralCode = surveyInstance.getValue('referral_code');
      Analytics.track('Survey Completed', {
        data: surveyInstance.data,
        referral_code: referralCode || undefined,
      });
      setCurrentQuestionIndex(surveyInstance.pages.length);
      navRef.current(getRedirectPath());
    };

    const handleValueChanging = (sender: SurveyModel, question: any) => {
      // Guard against calculated values or other non-question changes
      if (!question.question) {
        return;
      }

      // This handles clearing the options in a checkbox question when the user selects "prefer not to say"
      const isCheckboxQuestion = question.question.getType() === 'checkbox';
      const {oldValue, value} = question;
      if (isCheckboxQuestion) {
        if (oldValue?.includes('prefer_not_to_say')) {
          question.value = question.value.filter((v: string) => v !== 'prefer_not_to_say');
        } else if (value?.includes('prefer_not_to_say')) {
          question.value = ['prefer_not_to_say'];
        }
        if (oldValue?.includes('none_of_the_above')) {
          question.value = question.value.filter((v: string) => v !== 'none_of_the_above');
        } else if (value?.includes('none_of_the_above')) {
          question.value = ['none_of_the_above'];
        }
      }
    };

    const handleAfterRenderQuestion = (sender: SurveyModel, options: any) => {
      const question = options.question;
      if (question.getType() === 'radiogroup') {
        // Check if the question has already been answered.
        // If so, attach the click handler only to the selected answer
        // and prevent the normal click behavior to avoid "double click"
        // Otherwise do nothing.
        const isAnswered = question.value !== undefined;
        if (isAnswered) {
          const selectedChoice = question.choices.find((choice: any) => choice.value === question.value);
          const handleLabelClick = (event: MouseEvent) => {
            event.preventDefault();
            handleValueChanged(sender, {
              name: question.name,
              question: options.question,
              value: question.value,
            });
          };
          selectedChoice?._htmlElement?.addEventListener('click', handleLabelClick);
        }
      }

      if (options.question.getType() === 'text') {
        // Change the enter key hint to done for text input questions
        const inputElement = options.htmlElement.querySelector('input');
        if (inputElement) {
          inputElement.setAttribute('enterkeyhint', 'done');
        }
      }
      // For Name Question
      // Change the autocomplete and inputmode for the person_name question
      // Also add an event listener to the input element to hide the
      // keyboard when the user presses enter
      if (options.question.name === 'first_name') {
        const inputElement = options.htmlElement.querySelector('input');
        if (inputElement) {
          inputElement.setAttribute('autocomplete', 'given-name');
          inputElement.setAttribute('inputmode', 'text');
          inputElement.setAttribute('spellcheck', 'false');
          inputElement.addEventListener('keydown', (event: any) => {
            if (event.key === 'Enter') {
              event.preventDefault();

              // close keyboard but don't move to next page
              inputElement.blur(); // This will hide the keyboard
            }
          });
        }
      }

      if (options.question.name === 'email') {
        const inputElement = options.htmlElement.querySelector('input');
        if (inputElement) {
          inputElement.setAttribute('type', 'email');
          inputElement.setAttribute('inputmode', 'email');
          inputElement.setAttribute('autocomplete', 'email');
          inputElement.setAttribute('spellcheck', 'false');
        }
      }

      // For Name Question
      // Change the autocomplete and inputmode for the person_name question
      // Also add an event listener to the input element to hide the
      // keyboard when the user presses enter
      if (options.question.name === 'last_name') {
        const inputElement = options.htmlElement.querySelector('input');
        if (inputElement) {
          inputElement.setAttribute('autocomplete', 'family-name');
          inputElement.setAttribute('inputmode', 'text');
          inputElement.setAttribute('spellcheck', 'false');
          inputElement.addEventListener('keydown', (event: any) => {
            if (event.key === 'Enter') {
              event.preventDefault();

              // close keyboard but don't move to next page
              inputElement.blur(); // This will hide the keyboard
            }
          });
        }
      }

      // For Weight Question
      // Enable numeric keyboard for mobile devices
      if (options.question.name === 'weight_pounds') {
        const inputElement = options.htmlElement.querySelector('input');
        if (inputElement) {
          inputElement.setAttribute('type', 'tel');
          inputElement.setAttribute('inputmode', 'numeric');
          inputElement.setAttribute('pattern', '[0-9]*');
          inputElement.addEventListener('keydown', (event: any) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              inputElement.blur(); // This will hide the keyboard
            }
          });
        }
      }

      // Handle input masking/formatting based on question config
      if (options.question.getType() === 'text' && options.question.inputMask) {
        const inputElement = options.htmlElement.querySelector('input');
        if (inputElement && typeof options.question.inputMask === 'function') {
          options.question.inputMask(inputElement, question);
        }
      }
    };

    // Extract inputMask functions before JSON serialization (functions don't serialize)
    type InputMaskFunction = (inputElement: HTMLInputElement, question: any) => void;
    const inputMaskMap = new Map<string, InputMaskFunction>();
    surveyConfigJson.pages.forEach(page => {
      page.elements?.forEach((element: any) => {
        if (element.type === 'text' && element.inputMask) {
          inputMaskMap.set(element.name, element.inputMask);
        }
      });
    });

    // Handle duplicate page names by appending numbers
    const pagesWithUniqueName = handleDuplicatePageNames(surveyConfigJson.pages);
    const surveyConfigJsonWithUniquePages = {...surveyConfigJson, pages: pagesWithUniqueName};

    surveyInstance.fromJSON(surveyConfigJsonWithUniquePages);

    // Restore inputMask functions after JSON serialization
    surveyInstance.getAllQuestions().forEach((question: any) => {
      if (question.getType() === 'text' && inputMaskMap.has(question.name)) {
        question.inputMask = inputMaskMap.get(question.name);
      }
    });
    SurveyData.restore(surveyInstance);

    // Check if user is navigating back from checkout (browser back button or UI back button)
    // This MUST happen after restore to ensure data is available
    const navigatingBack = sessionStorage.getItem('insomnia-navigating-back-from-checkout') === 'true';
    if (navigatingBack) {
      // Clear the flag
      sessionStorage.removeItem('insomnia-navigating-back-from-checkout');
      // Reset survey instance state to allow editing
      // If the instance is in completed state, we need to clear and restore it properly
      if (surveyInstance.state === 'completed') {
        const savedData = SurveyData.read();
        if (savedData && savedData.pageNo !== undefined) {
          // Save the page number before clearing
          const savedPageNo = savedData.pageNo;
          // Clear the instance to reset the state
          surveyInstance.clear();
          // Restore the survey structure by calling fromJSON again
          surveyInstance.fromJSON(surveyConfigJsonWithUniquePages);
          // Restore inputMask functions
          surveyInstance.getAllQuestions().forEach((question: any) => {
            if (question.getType() === 'text' && inputMaskMap.has(question.name)) {
              question.inputMask = inputMaskMap.get(question.name);
            }
          });
          // Restore the data
          surveyInstance.data = savedData;
          // Set the page number
          const targetPage = Math.min(savedPageNo, surveyInstance.pages.length - 1);
          surveyInstance.currentPageNo = Math.max(0, targetPage);
        }
      }
    } else {
      // If saved data says completed: false but survey instance is completed,
      // we need to reset it - this handles edge cases
      const savedData = SurveyData.read();
      if (savedData?.completed === false && surveyInstance.state === 'completed') {
        // Save page number before clearing
        const savedPageNo = savedData.pageNo;
        if (savedPageNo !== undefined) {
          // Clear and restore to reset state
          surveyInstance.clear();
          surveyInstance.fromJSON(surveyConfigJsonWithUniquePages);
          // Restore inputMask functions
          surveyInstance.getAllQuestions().forEach((question: any) => {
            if (question.getType() === 'text' && inputMaskMap.has(question.name)) {
              question.inputMask = inputMaskMap.get(question.name);
            }
          });
          // Restore data and page number
          surveyInstance.data = savedData;
          const targetPage = Math.min(savedPageNo, surveyInstance.pages.length - 1);
          surveyInstance.currentPageNo = Math.max(0, targetPage);
        }
      }
    }

    setInitialSurveyState(surveyInstance); //set the initial Survey State
    // If survey was cleared and restored, ensure current page is properly set
    // This fixes the issue where the survey body is blank after navigating back
    if (surveyInstance.currentPage) {
      handleCurrentPageChanged(surveyInstance);
    }
    setIsLoading(false);

    surveyInstance.onAfterRenderQuestion.add(handleAfterRenderQuestion);
    surveyInstance.onValueChanging.add(handleValueChanging);
    surveyInstance.onValueChanged.add(handleValueChanged);
    surveyInstance.onCurrentPageChanged.add(handleCurrentPageChanged);
    surveyInstance.onComplete.add(handleComplete);

    return () => {
      surveyInstance.onAfterRenderQuestion.remove(handleAfterRenderQuestion);
      surveyInstance.onValueChanging.remove(handleValueChanging);
      surveyInstance.onValueChanged.remove(handleValueChanged);
      surveyInstance.onCurrentPageChanged.remove(handleCurrentPageChanged);
      surveyInstance.onComplete.remove(handleComplete);
    };
  }, [surveyConfigJson, getRedirectPath]);

  // Read referral code from route params, location state, or URL query params and save to survey data
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Priority: route params > location state > query params
    const referralCode =
      params.referralCode ??
      (location.state as {referralCode?: string})?.referralCode ??
      searchParams.get('ref') ??
      searchParams.get('referral');
    if (referralCode) {
      const existingReferralCode = surveyInstance.getValue('referral_code');
      // Only set if it doesn't already exist (don't overwrite if user already has one)
      if (!existingReferralCode) {
        surveyInstance.setValue('referral_code', referralCode);
        SurveyData.save(surveyInstance);
        // Track referral code capture
        Analytics.track('Referral Code Captured', {
          referral_code: referralCode,
          source: params.referralCode
            ? 'route_params'
            : (location.state as {referralCode?: string})?.referralCode
              ? 'location_state'
              : 'query_params',
        });
        // Set referral code as user attribute
        Analytics.identify({referral_code: referralCode});
      }
      // Remove referral code from URL after saving (only if it was in query params)
      if (searchParams.get('ref') || searchParams.get('referral')) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('ref');
        newSearchParams.delete('referral');
        const newSearch = newSearchParams.toString();
        const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [isLoading, searchParams, location.state, params.referralCode]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    let isCancelled = false;

    const applyStateLocation = (stateCode: string) => {
      const normalizedState = stateCode.toUpperCase();
      const currentStateLocationRaw = surveyInstance.getValue('state_location');
      const currentStateLocation =
        typeof currentStateLocationRaw === 'string' ? currentStateLocationRaw.toUpperCase() : undefined;
      let shouldPersist = false;

      if (normalizedState.length === 2 && currentStateLocation !== normalizedState) {
        surveyInstance.setValue('state_location', normalizedState);
        surveyInstance.setValue('insurance_state', normalizedState);
        shouldPersist = true;
      }

      const insuranceQuestion = surveyInstance.getQuestionByName('insurance_coverage');
      if (insuranceQuestion) {
        const previousValue = insuranceQuestion.value;
        const updatedChoices = getInsuranceOptionsForState(normalizedState);
        insuranceQuestion.choices = updatedChoices;

        const valueStillValid = updatedChoices.some(choice => choice.value === previousValue);
        if (previousValue && !valueStillValid) {
          insuranceQuestion.clearValue();
          shouldPersist = true;
        }
      }

      if (shouldPersist) {
        SurveyData.save(surveyInstance);
      }
    };

    const existingStateLocationRaw = surveyInstance.getValue('state_location');
    const existingStateLocation =
      typeof existingStateLocationRaw === 'string' ? existingStateLocationRaw.toUpperCase() : undefined;
    if (existingStateLocation && existingStateLocation.length === 2) {
      applyStateLocation(existingStateLocation);
      return;
    }

    const fetchLocation = async () => {
      try {
        const geoInfo = await getGeoLocation();
        if (!geoInfo || isCancelled) {
          return;
        }

        const {region_code} = geoInfo;
        if (!region_code || region_code.length !== 2) {
          return;
        }
        Analytics.identify({stateCode: region_code});

        applyStateLocation(region_code);
      } catch (error) {
        console.error('Failed to fetch geo location for survey', error);
      }
    };

    fetchLocation();

    return () => {
      isCancelled = true;
    };
  }, [isLoading]);

  const showLogo = surveyInstance?.currentPage?.jsonObj?.riseProperties?.showLogo;
  const theme = surveyInstance?.currentPage?.jsonObj?.riseProperties?.theme;
  const themeName = typeof theme === 'function' ? theme() : theme;
  const previousPage = surveyInstance.visiblePages[surveyInstance.currentPageNo - 1];
  const disableBackToAccountCreation =
    (previousPage as PageModel)?.elements[0]?.name === 'account_creation_interstitial';
  // Pages can opt out of the back arrow (e.g. the multi-step intake wizard, whose
  // internal steps SurveyJS can't navigate — page-level back would jump out of it).
  const hideBackButton = Boolean(surveyInstance?.currentPage?.jsonObj?.riseProperties?.hideBackButton);

  useEffect(() => {
    console.log('InsomniaSurvey mounted');
  }, []);

  // Handle browser back button to navigate to previous question
  useEffect(() => {
    let isHandlingPopState = false;

    const handlePopState = () => {
      // On a back-suppressed page (e.g. the intake wizard), swallow browser-back too
      // so the gesture can't jump out of the wizard — re-push the popped entry.
      if (surveyInstance?.currentPage?.jsonObj?.riseProperties?.hideBackButton) {
        window.history.pushState({surveyPage: surveyInstance.currentPageNo}, '', window.location.href);
        return;
      }
      // Only handle if we're not on the first page and back button is enabled
      if (!isHandlingPopState && !isFirstPage && !disableBackToAccountCreation && surveyInstance.currentPageNo > 0) {
        isHandlingPopState = true;
        // Navigate to previous page in survey (same as header back button)
        surveyInstance.prevPage();
        // Replace current state to update the history without adding a new entry
        window.history.replaceState({surveyPage: surveyInstance.currentPageNo}, '', window.location.href);
        isHandlingPopState = false;
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isFirstPage, disableBackToAccountCreation]);

  // Push history state when moving forward in survey (creates history entries for each page)
  useEffect(() => {
    // Only push state when moving forward (not on initial load)
    // This creates a history entry for each survey page so back button works
    if (currentQuestionIndex > 0 && !isLoading) {
      window.history.pushState({surveyPage: currentQuestionIndex}, '', window.location.href);
    }
  }, [currentQuestionIndex, isLoading]);

  useEffect(() => {
    const clearTheme = () => {
      document.body.classList.forEach(className => {
        if (className.endsWith('-theme')) {
          document.body.classList.remove(className);
        }
      });
    };

    clearTheme();

    if (themeName) {
      // Apply css theme to the site
      document.body.classList.add(`${themeName}-theme`);
    }
  }, [themeName]);
  return (
    <ErrorBoundary>
      <SurveyErrorProvider>
        {/* `osa-shell` opts this survey into the OSA funnel's
            onboarding.risescience.com shell treatment (desktop card +
            page bg, scoped in osaShell.css). The insomnia clinic intake
            keeps the plain shell. */}
        <div className={`container${surveyConfigJson.title === 'osa_funnel' ? ' osa-shell' : ''}`}>
          <div>
            <RiseHeader
              backButton={!isFirstPage && !disableBackToAccountCreation && !hideBackButton}
              onBackClick={() => {
                surveyInstance?.prevPage();
              }}
              enableCategoryHeader={hasCategories}
              hideCategory={hideCategory}
              category={category}
              showLogo={showLogo || (isFirstPage && !surveyConfigJson.showCategoryOnFirstPage)}
            />
            {hideProgressBar ? null : (
              <div
                className={surveyConfigJson.title === 'osa_funnel' ? 'osa-progress-wrap' : undefined}
                style={{paddingTop: 8, paddingBottom: 24}}
              >
                {hasCategories ? (
                  <CategoryProgressBar pageNumber={surveyInstance?.currentPageNo ?? 0} categories={categories} />
                ) : (
                  <SegmentedProgressBar
                    currentQuestionIndex={currentQuestionIndex}
                    totalQuestions={surveyInstance?.pages.length ?? 0}
                  />
                )}
              </div>
            )}
          </div>

          <div className={enableAnswerNewStyle ? `rise-radio-new-style survey-page-transition ${transitionClass}` : ''}>
            {!isLoading && surveyInstance ? <Survey model={surveyInstance} /> : <LoadingScreen />}
          </div>
          <SurveyErrorDisplay />
          <SocialProofFooterItem
            currentScreenIndex={currentQuestionIndex}
            config={surveyConfigJson.socialProofFooter ?? [{screenIndex: 2, itemId: 'disclaimer'}]}
          />
          <RiseSurveyControls
            isLastPage={isLastPage}
            isInterstitial={isInterstitialPage}
            isAutoAdvance={isAutoAdvance}
            isRequired={isRequiredQuestion}
          />
        </div>
      </SurveyErrorProvider>
    </ErrorBoundary>
  );
};
export default InsomniaSurvey;
